import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, CreditCard, Link2Off, Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { useNoIndexMeta } from '@shared/hooks';
import { formatMoney } from '@shared/utils/formatMoney';
import { cn } from '@shared/utils/cn';
import { ApiError } from '@core/api/apiClient';
import { captureApiError, capturePaymentError } from '@core/sentry';
import { useRestaurant } from '@core/tenant/TenantProvider';
import BookingHeader from '@features/booking/components/BookingHeader';
import BookingFooter from '@features/booking/components/BookingFooter';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import GuaranteeChangeNotice from '@features/booking/components/GuaranteeChangeNotice';
import MercadoPagoCardFields, { type MercadoPagoCardHandle } from '@features/booking/components/payment/MercadoPagoCardFields';
import { CardInputError } from '@features/booking/utils/cardInput';
import { GUARANTEE_REFUSAL } from '../api/guaranteeAdjustment';
import { fetchAdjustmentLink, isAdjustmentToken, LINK_SOURCE, NO_LONGER_PAYABLE, payAdjustmentLink, toAdjustmentLink } from '../api/guaranteeAdjustmentLink';
import { formatReservationDate, formatReservationTime } from './reservationManage';

const PRIMARY_CTA = 'bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md';
const PAY_SOURCE = 'guarantee-adjustment-pay';

/** What the last Pay answered. `ends`: paying again from here cannot help (a declined stored card, an outcome not yet known), so Pay goes. `info`: not a failure. */
type Notice = { key: string; ends?: boolean; info?: boolean };

const isGone = (err: unknown) => err instanceof ApiError && (err.status === 404 || err.status === 410);

/**
 * "Mon, 2 Nov, 23:59" in the guest's locale and the venue's time zone, as the booking's date and time are shown, on
 * the same 24-hour clock; in the browser's zone when the venue names none it knows. Null without an expiry.
 */
const formatExpiry = (iso: string | null, timeZone: string | undefined) => {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
  try {
    return at.toLocaleString(undefined, { ...options, timeZone: timeZone || undefined });
  } catch {
    return at.toLocaleString(undefined, options); // RangeError: a zone this browser does not know
  }
};

/**
 * The guest pays a deposit difference staff opened by raising the party, from the emailed link
 * (`/guarantee-adjustment/:token`): the stored card on Stripe, a card entered here on MercadoPago.
 */
const GuaranteeAdjustmentPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const restaurant = useRestaurant();
  const queryClient = useQueryClient();
  // The URL carries the token, so keep it out of search indexes.
  useNoIndexMeta();

  const validToken = isAdjustmentToken(token);
  const queryKey = ['guaranteeAdjustmentLink', token];
  const { data: answer, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => fetchAdjustmentLink(token!),
    enabled: validToken,
    retry: false,
    // The amount on screen is the one Pay echoes: never swapped under the guest by a refocus.
    staleTime: Infinity,
    // Kept only while shown: a page mounted again (after paying, say) asks again rather than offering Pay from the cache.
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { silentOn: [404, 410], source: LINK_SOURCE },
  });
  const [outcome, setOutcome] = useState<'paid' | 'processing' | 'invalid' | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const cardRef = useRef<MercadoPagoCardHandle>(null);

  const gone = answer === NO_LONGER_PAYABLE;
  const data = gone ? undefined : answer;
  const link = data?.link;
  const owed = link?.adjustment.outstanding;
  const processor = owed?.processor ?? null;
  // The card form mints its token with this venue's MercadoPago key: a venue that serves none cannot take the card.
  const charger = processor === 'stripe' || (processor === 'mercadopago' && restaurant.paymentProcessor === 'mercadopago') ? processor : null;

  const refused = (err: unknown, by: 'stripe' | 'mercadopago') => {
    const answer = err instanceof ApiError ? err : null;
    const body = (answer?.body ?? {}) as { reason?: unknown; adjustment?: unknown };
    if (isGone(err)) return setOutcome('invalid');
    if (body.reason === GUARANTEE_REFUSAL.QUOTE_CHANGED) {
      // The amount moved: show the new one and let the guest confirm it. The backend sends the fresh block as
      // `adjustment` only while the row is payable, so no block, or a row no longer payable, is a link no longer valid.
      const fresh = body.adjustment == null ? NO_LONGER_PAYABLE : toAdjustmentLink(body.adjustment);
      if (fresh === NO_LONGER_PAYABLE) return setOutcome('invalid');
      if (!fresh || !data) {
        // A block this app cannot read is not a link that is gone: the GET has the quote to pay.
        captureApiError(err, { source: PAY_SOURCE });
        void refetch();
      } else {
        queryClient.setQueryData(queryKey, { ...data, link: fresh });
      }
      return setNotice({ key: 'GUARANTEE_CHANGE_QUOTE_CHANGED', info: true });
    }
    if (answer?.status === 402 && body.reason !== GUARANTEE_REFUSAL.PAYMENT_IN_PROGRESS) {
      // A stored card that needs the guest's authentication cannot get it here.
      if (body.reason === GUARANTEE_REFUSAL.PAYMENT_REQUIRES_ACTION) capturePaymentError(err, by, { source: PAY_SOURCE });
      return setNotice(by === 'stripe' ? { key: 'GUARANTEE_ADJUSTMENT_DECLINED_STORED', ends: true } : { key: 'GUARANTEE_ADJUSTMENT_DECLINED' });
    }
    // The charge may have gone through: no answer, a payment still in flight, or a server that failed part-way.
    const inProgress = body.reason === GUARANTEE_REFUSAL.PAYMENT_IN_PROGRESS;
    const unknownOutcome = !answer || answer.isNetworkError() || (answer.status ?? 0) >= 500 || inProgress;
    // A dropped connection and a payment in flight are the guest's situation, not a fault.
    if (!answer?.isNetworkError() && !inProgress) captureApiError(err, { source: PAY_SOURCE });
    setNotice(unknownOutcome ? { key: 'GUARANTEE_CHANGE_PAYMENT_IN_PROGRESS', ends: true, info: true } : { key: 'RESERVATION_STATUS_ERROR' });
  };

  const pay = async () => {
    if (!owed || !charger || inFlight.current || notice?.ends) return;
    inFlight.current = true;
    setSubmitting(true);
    setNotice(null);
    try {
      let card: Awaited<ReturnType<MercadoPagoCardHandle['tokenize']>> | undefined;
      if (charger === 'mercadopago') {
        try {
          card = await cardRef.current!.tokenize();
        } catch (err) {
          // Nothing was sent: the guest corrects the card, or tries again.
          if (!(err instanceof CardInputError)) capturePaymentError(err, 'mercadopago', { source: PAY_SOURCE, stage: 'token' });
          return setNotice({ key: err instanceof CardInputError ? err.message : 'TRADITIONAL_GRAB_ERROR' });
        }
      }
      setOutcome(await payAdjustmentLink(token!, { quotedOutstanding: owed.amount, ...card }));
    } catch (err) {
      refused(err, charger);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const money = (amount: number, currency: string) => formatMoney(amount, currency, i18n.language);

  const state = (icon: ReactNode, title: string, subtitle: string | null, action: ReactNode) => (
    <div className="flex flex-col items-center w-full max-w-sm px-4 text-center">
      {icon}
      <h1 className="text-3xl sm:text-4xl font-display mb-2">{title}</h1>
      {subtitle && <p className="text-sm text-foreground/50 mb-8">{subtitle}</p>}
      {action}
    </div>
  );
  // Replaces the pay link in the history: Back would show its token URL to the trackers home loads.
  const backHome = <Button className={PRIMARY_CTA} onClick={() => navigate('/', { replace: true })}>{t('CONFIRM_GUARANTEE_BACK_TO_HOME')}</Button>;
  const badge = (icon: ReactNode) => <div className="w-16 h-16 rounded-full bg-brand-muted text-brand flex items-center justify-center mb-6">{icon}</div>;

  const content = () => {
    if (!validToken || outcome === 'invalid' || gone || isGone(error)) {
      return state(<Link2Off size={48} aria-hidden className="text-foreground/40 mb-6" />, t('GUARANTEE_ADJUSTMENT_INVALID_TITLE'), t('GUARANTEE_ADJUSTMENT_INVALID_SUBTITLE'), backHome);
    }
    if (outcome === 'paid' && link) {
      return state(badge(<CheckCircle2 size={32} aria-hidden />), t('GUARANTEE_ADJUSTMENT_PAID_TITLE'),
        t('GUARANTEE_ADJUSTMENT_PAID_SUBTITLE', { amount: money(link.adjustment.newFee, link.adjustment.currency) }), backHome);
    }
    if (outcome === 'processing') {
      return state(badge(<Clock size={32} aria-hidden />), t('GUARANTEE_ADJUSTMENT_PROCESSING_TITLE'), t('GUARANTEE_ADJUSTMENT_PROCESSING_SUBTITLE'), backHome);
    }
    if (error && !isFetching) {
      return state(<TriangleAlert size={48} aria-hidden className="text-foreground/40 mb-6" />, tErrors('GENERAL_SOMETHING_WENT_WRONG'), null,
        <Button className={`${PRIMARY_CTA} mt-6`} onClick={() => void refetch()}>{tErrors('ACTIONS_RETRY')}</Button>);
    }
    if (!data || !link || !owed) {
      return (
        <div className="text-center text-foreground/40 text-sm py-12">
          <Loader2 size={24} aria-hidden className="animate-spin mx-auto mb-2" />
          {t('CONFIRM_GUARANTEE_LOADING')}
        </div>
      );
    }
    const expiry = formatExpiry(link.expiresAt, restaurant.timezone);
    // The card that pays and Pay itself go together: neither shows once paying from here cannot help.
    const payable = charger && !notice?.ends ? charger : null;
    return (
      <>
        {badge(<CreditCard size={32} aria-hidden />)}
        <h1 className="text-3xl sm:text-4xl font-display mb-2 text-center px-4">{t('GUARANTEE_ADJUSTMENT_TITLE')}</h1>
        <p className="text-sm text-foreground/50 mb-8 text-center max-w-sm px-4">{t('GUARANTEE_ADJUSTMENT_SUBTITLE')}</p>
        <div className="w-full max-w-sm px-4 space-y-4">
          <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
            <SummaryRow label={t('CONFIRMATION_RESTAURANT')} value={restaurant.name} />
            {data.booking.resDate && <SummaryRow label={t('CONFIRMATION_DATE')} value={formatReservationDate(data.booking.resDate)} />}
            {data.booking.resTime && <SummaryRow label={t('CONFIRMATION_TIME')} value={formatReservationTime(data.booking.resTime)} />}
            <SummaryRow label={t('CONFIRMATION_GUESTS')} value={`${link.previousPartySize} → ${link.newPartySize}`} />
          </div>
          <GuaranteeChangeNotice adjustment={link.adjustment} />
          {expiry && <p className="text-xs text-foreground/50 text-center">{t('GUARANTEE_ADJUSTMENT_EXPIRES', { date: expiry })}</p>}
          {payable === 'stripe' && <p className="text-sm text-foreground/70 text-center">{t('GUARANTEE_CHANGE_STORED_CARD')}</p>}
          {payable === 'mercadopago' && <MercadoPagoCardFields ref={cardRef} allowAMEXBR={restaurant.allowAMEXBR} />}
          {!charger && <p className="text-sm text-foreground/70 text-center">{t('GUARANTEE_ADJUSTMENT_CONTACT')}</p>}
          {notice && (
            <p role="alert" className={cn('rounded-xl p-4 text-sm', notice.info ? 'bg-brand-muted text-foreground' : 'bg-destructive/10 text-destructive')}>
              {t(notice.key)}
            </p>
          )}
          {payable && (
            <Button className={`${PRIMARY_CTA} disabled:opacity-50`} onClick={pay} disabled={submitting || isFetching}>
              {submitting
                ? <><Loader2 size={18} aria-hidden className="mr-2 animate-spin" />{t('PAYMENT_PROCESSING')}</>
                : t('GUARANTEE_ADJUSTMENT_PAY', { amount: money(owed.amount, owed.currency) })}
            </Button>
          )}
        </div>
      </>
    );
  };

  return (
    <BookingChrome flexCol>
      <BookingHeader withoutExits />
      <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">{content()}</main>
      <footer className="border-t border-border">
        <BookingFooter />
      </footer>
    </BookingChrome>
  );
};

export default GuaranteeAdjustmentPage;
